import {observer} from "mobx-react-lite";
import {useStores} from "~/stores";
import type {GridInitialState, GridSlotProps} from "@mui/x-data-grid-premium";
import {GRID_AGGREGATION_FUNCTIONS, GRID_TREE_DATA_GROUPING_FIELD, useGridApiRef} from "@mui/x-data-grid-premium";
import {toJS} from "mobx";
import React from "react";
import {getBaseMuixDataGridSource} from "~/components/muix-data-grid/BaseTableDataModel.ts";
import {LocalizationProvider} from "@mui/x-date-pickers-pro";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {getDayJsLocale} from "~/utils/day-js-utils.ts";
import {MuixDebugComponent} from "~/components/muix-data-grid/MuixDebugComponent.tsx";
import {environment} from "~/env.ts";
import {Box, Button} from "@mui/material";
import NextPlanIcon from "@mui/icons-material/NextPlan";
import {CONTENT_COLLAPSED_WIDTH, CONTENT_EXPANDED_WIDTH} from "~/components/main/drawer/DrawerConstants.ts";
import {WithStickyComponent} from "~/components/muix-data-grid/WithStickyComponent.tsx";
import {DEFAULT_PAGE_SIZE_OPTIONS} from "~/components/muix-data-grid/constants/Pagination.ts";
import {SCToolbar} from "./SCToolbar.tsx";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid.tsx";

export const SCTransactionDetailTableComponent = observer(() => {
    const {rootAppStore, p, authStore, bagStore, spendConcentrationStore} = useStores();
    const pageController = spendConcentrationStore.sCPageController;
    const tableController = pageController.tableController;
    const filterController = pageController.filterController;
    const reviewApi = pageController.reviewApi;
    const databag = bagStore.bagId;
    const apiRef = useGridApiRef();
    const filterModel = toJS(filterController.gridFilterModel);

    React.useEffect(() => {
        // Link the apiRef to the store, so that the store can use it
        if (pageController)
            // noinspection TypeScriptUnresolvedReference
            pageController.tableController.gridApi = apiRef.current ?? undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiRef]);
    const initialState: GridInitialState = React.useMemo<GridInitialState>(() => ({
        pagination: {
            rowCount: -1,
        },
    }), []);
    const dataSource = React.useMemo(() =>
        databag ? getBaseMuixDataGridSource(databag, pageController, tableController, filterController,
            reviewApi, pageController.relatedDataFieldsSpec) : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [databag, p.p, bagStore.configStore.databagConfig]
    );
    React.useEffect(() => {
        if (!tableController.refreshData) return;
        // noinspection TypeScriptUnresolvedReference
        apiRef.current?.dataSource.cache.clear();
        // noinspection TypeScriptUnresolvedReference
        apiRef.current?.setRows([]);
        // noinspection TypeScriptUnresolvedReference
        apiRef.current?.dataSource.fetchRows();
    }, [tableController.refreshData])
    const [stateCounter, setStateCounter] = React.useState(0);


    return <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={getDayJsLocale(p.p.locale)}>
        <MuixDebugComponent tableController={tableController} filterController={filterController}/>
        {environment.isTest && authStore.isMithraStaff && <Button
            startIcon={<NextPlanIcon/>}
            onClick={() => setStateCounter(stateCounter + 1)}
        >
            State counter: {stateCounter}
        </Button>}
        <Box sx={{width: rootAppStore.isDrawerOpen ? CONTENT_EXPANDED_WIDTH : CONTENT_COLLAPSED_WIDTH}}>
            <WithStickyComponent stickyHeader stickyScrollbar topPadding={0}>
                <CustomDataGrid
                    className="gr-table-muix base-table-muix auto-row-height-centered"
                    initialState={initialState}
                    // pinning
                    pinnedColumns={tableController.pinnedColumns}
                    onPinnedColumnsChange={(pinnedColumns, details) => tableController.onPinnedColumnsChange(pinnedColumns, details)}
                    // col width
                    onColumnWidthChange={tableController.storeColumnWidthChange}
                    disableColumnResize={tableController.autoResize}
                    // col order
                    onColumnOrderChange={tableController.storeColumnOrderChange}
                    // sorting
                    sortModel={tableController.sortModel}
                    onSortModelChange={(sortModel, details) => tableController.onSortModelChange(sortModel, details)}
                    // Column visibility
                    disableColumnSelector
                    // 1920 * 4
                    columnBufferPx={7680}
                    disableVirtualization
                    columnVisibilityModel={tableController.visibilityModel}
                    onColumnVisibilityModelChange={(model, details) => tableController.onVisibilityModelChange(model, details)}
                    // Pagination
                    pagination
                    pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
                    paginationModel={tableController.paginationModel}
                    onPaginationModelChange={(paginationModel, details) => tableController.onPaginationModelChange(paginationModel, details)}
                    onPaginationMetaChange={params => console.debug('onPaginationMetaChange', params)}
                    headerFilters
                    filterModel={filterModel}
                    onFilterModelChange={(model, details) => filterController.onFilterModelChange(model, details)}
                    // Grouping
                    groupingColDef={tableController.groupingColDef}
                    rowGroupingModel={tableController.rowGroupingModel}
                    onRowGroupingModelChange={(model, details) => tableController.onRowGroupingModelChange(model, details)}
                    rowGroupingColumnMode="multiple"
                    // Grouping - aggregation
                    aggregationFunctions={GRID_AGGREGATION_FUNCTIONS}
                    aggregationModel={tableController.aggregationModel}
                    // Data
                    apiRef={apiRef}
                    columns={tableController.tableColumns}
                    dataSource={dataSource}
                    loading={tableController.isLoadingData}
                    onDataSourceError={error => pageController.onError(error)}
                    // NOTE: When there is no grouping allowed on the table DO NOT use tree data, it would mess things up
                    getRowId={(row) => tableController.rowId(row)}
                    disableChildrenSorting
                    disablePivoting
                    disableRowSelectionOnClick
                    // Toolbars
                    showToolbar
                    slots={{
                        toolbar: SCToolbar,
                    }}
                    slotProps={{
                        toolbar: {
                            settings: tableController.tableSettings,
                            // onSettingsChange: (settings: BaseTableSettings) => tableController.onSettingsChange(settings),
                        },
                        columnsManagement: {
                            getTogglableColumns: columns => {
                                const aggregationOnlyFields = tableController.tableAggregationOnlyColumns.map(col => col.field);
                                return columns.map(c => c.field).filter(f => !f.startsWith('__') &&
                                    !aggregationOnlyFields.includes(f) && !tableController.rowGroupingModel.includes(f))
                            },
                            searchDebounceMs: 500,
                        },
                        loadingOverlay: {
                            variant: 'linear-progress',
                            noRowsVariant: 'linear-progress',
                        },
                        basePagination: {
                            disabled: tableController.muixCustomizedPaginationDisabled,
                            count: tableController.muixCustomizedPaginationCount,
                            page: tableController.muixCustomizedPaginationPage,
                            rowsPerPage: tableController.muixCustomizedPaginationRowsPerPage,
                            rowsPerPageOptions: DEFAULT_PAGE_SIZE_OPTIONS,
                            onPageChange: (_e, p) => tableController.muixCustomizedPaginationOnPageChange(p),
                            onRowsPerPageChange: (rpp) => tableController.muixCustomizedPaginationOnRowsPerPageChange(rpp),
                        },
                    } as unknown as GridSlotProps}
                    // Visual configurations
                    getRowHeight={() => 'auto'}
                    getRowClassName={(params) => {
                        if (!params.row) return '';
                        return 'muix-row'
                            + (tableController.loadingRowIds.has(tableController.rowId(params.row)) ? ' loading-row' : (
                                params.row.path ? ' tree-level-' + String(params.row.path.length) : ''))
                            + (tableController.paginationDisabledRowIds.has(tableController.rowId(params.row)) ? ' pagination-disabled-row' : '')
                            + (tableController.paginationHiddenRowIds.has(tableController.rowId(params.row)) ? ' hidden-row' : '')
                            + (params.row['subGroupPagination'] ? ' sub-group-pagination-row' : '')
                    }}
                    getCellClassName={(params) => {
                        if (!params.row) return '';
                        return 'muix-cell'
                            + (params.field === GRID_TREE_DATA_GROUPING_FIELD ? ' grouping-cell' : '')
                            + (params.rowNode.type === 'leaf' ? ' leaf-cell' : '')
                            // Hide all other cells for the MoreRowsDummy row
                            + (params.row['subGroupPagination'] && params.field !== GRID_TREE_DATA_GROUPING_FIELD ? ' hidden-cell' : '')
                            + (params.row['subGroupPagination'] && params.field === GRID_TREE_DATA_GROUPING_FIELD ? ' sub-group-pagination' : '')
                    }}
                />
            </WithStickyComponent>
        </Box>
    </LocalizationProvider>
})
